import type { SegmentMst } from '@libs/domain';
import type { MstRangeSearchProps, MstSearchDialogProps, MstSearchProps } from '@libs/templates/mst';
import { MstSearchDialogTmpl, mstListSearchFieldConfig } from '@libs/templates/mst';
import type { Characters, FieldErrorField, Item, PageProps, WithExclusion } from '@ps-smart-cp/ppfw-client';
import {
  MasterRangeSearchField,
  MasterSearch,
  MasterSearchField,
  useValidator,
  withRerender,
} from '@ps-smart-cp/ppfw-client';
import { type Ref, forwardRef, useImperativeHandle } from 'react';
import { useSegmentMstSettings } from '../hooks';
import type { SegmentMstRegistration } from '../models';
import { SegmentMstListSearch } from '../models';
import { fetchMst, listRequest } from '../repositories';
import { createHigherSegmentMstItem, createSegmentMstItemName } from '../services';
import { genSegmentMstColumns } from './GridColumns';
import { highOrderAdditionalInfo } from './highOrderAdditionalInfo';

function InnerComponent({
  segmentMstKey,
  ...restProps
}: MstSearchDialogProps<SegmentMstRegistration> & PageProps & Pick<SegmentMst['schema'], 'segmentMstKey'>) {
  const segmentMstSettings = useSegmentMstSettings();

  return (
    <MstSearchDialogTmpl
      mstName={createSegmentMstItemName(segmentMstKey, segmentMstSettings)}
      columns={genSegmentMstColumns(segmentMstKey)}
      items={{
        ...mstListSearchFieldConfig,
        ...createHigherSegmentMstItem(segmentMstKey, segmentMstSettings),
      }}
      listRequestFunction={listRequest(segmentMstKey)()}
      initMst={SegmentMstListSearch.init}
      {...restProps}
    >
      {highOrderAdditionalInfo(segmentMstKey)}
    </MstSearchDialogTmpl>
  );
}

const Dialog = withRerender(InnerComponent, 'SegmentMstDialog');

function highOrderSegmentMstSearchField<K extends 'higherRankSegmentCode' | 'segmentMstCode'>(_: K) {
  return function SegmentMasterSearchField<
    T extends Pick<SegmentMst['schema'], K>,
    I extends Record<K, Item<Characters>>,
  >({ segmentMstKey, ...restProps }: MstSearchProps<T, K, I> & Pick<SegmentMst['schema'], 'segmentMstKey'>) {
    return (
      <MasterSearchField getMaster={async (code) => fetchMst(segmentMstKey)()({ code })} {...restProps}>
        {(setIsOpen, setMaster, getMst) => (
          <Dialog
            setIsOpen={setIsOpen}
            setMaster={setMaster}
            segmentMstKey={segmentMstKey}
            getMaster={getMst}
          />
        )}
      </MasterSearchField>
    );
  };
}

function highOrderSegmentMstSearch<K extends 'segmentMstCode'>(_: K) {
  return function SegmentMasterSearch<
    T extends Pick<SegmentMst['schema'], K>,
    I extends Record<K, Item<Characters>>,
  >({ segmentMstKey, ...restProps }: MstSearchProps<T, K, I> & Pick<SegmentMst['schema'], 'segmentMstKey'>) {
    return (
      <MasterSearch
        getMaster={async (code) => fetchMst(segmentMstKey)()({ code })}
        itemAttribute={restProps.items[restProps.prop].attribute}
        {...restProps}
      >
        {(setIsOpen, setMaster, getMst) => (
          <Dialog
            setIsOpen={setIsOpen}
            setMaster={setMaster}
            segmentMstKey={segmentMstKey}
            getMaster={getMst}
          />
        )}
      </MasterSearch>
    );
  };
}

const SegmentMstSearchField = highOrderSegmentMstSearchField('segmentMstCode');

const SegmentMstSearch = highOrderSegmentMstSearch('segmentMstCode');

const HigherRankSegmentMstSearch = highOrderSegmentMstSearchField('higherRankSegmentCode');

interface SegmentMstRangeSearchRef {
  validate: () => FieldErrorField[];
}

function SegmentMstRangeSearchInnerComponent<
  T extends Pick<SegmentMst['schema'], K>,
  K extends 'segmentMstCodeRange',
  I extends Record<K, Item<WithExclusion>>,
>(
  props: MstRangeSearchProps<T, K, I> & Pick<SegmentMst['schema'], 'segmentMstKey'>,
  ref: Ref<SegmentMstRangeSearchRef>,
) {
  const { segmentMstKey, ...restProps } = props;
  const { obj, items, index } = restProps;
  const validator = useValidator();
  const validate = () => {
    return validator
      .with(obj, items)
      .execute()
      .map((s) => ({ ...s, field: `${s.field}-${index}` }));
  };
  useImperativeHandle(ref, () => ({ validate }));

  return (
    <MasterRangeSearchField getMaster={async (code) => fetchMst(segmentMstKey)()({ code })} {...restProps}>
      {(setIsOpen, setMaster, getMst) => (
        <Dialog
          setIsOpen={setIsOpen}
          setMaster={setMaster}
          segmentMstKey={segmentMstKey}
          getMaster={getMst}
        />
      )}
    </MasterRangeSearchField>
  );
}

const SegmentMstRangeSearch = forwardRef(SegmentMstRangeSearchInnerComponent);

export {
  HigherRankSegmentMstSearch,
  highOrderSegmentMstSearch,
  highOrderSegmentMstSearchField,
  SegmentMstRangeSearch,
  SegmentMstSearch,
  SegmentMstSearchField,
};

export type { SegmentMstRangeSearchRef };
